import React, { useState, useEffect, useCallback, useId } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Trash2, PenLine, FileText, Calendar } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { Dialog, Tabs, EmptyState, ErrorState, TableSkeleton, PersonAvatar, fmtDate, fmtTime, fmtAgo } from '../../components/ui';
import StaffHeader from './StaffHeader';
import { useStaffRecord, useStableCallback, IconButton } from './hrKit';

const asArray = (v) => (Array.isArray(v) ? v : (typeof v === 'string' ? (() => { try { return JSON.parse(v); } catch { return []; } })() : []));
const showDate = (v) => (v ? fmtDate(String(v).slice(0, 10)) : '');

function ShiftLine({ note }) {
  if (!note.shift_date) return null;
  return (
    <p className="flex items-center gap-1.5 text-[12.5px] text-slate-500">
      <Calendar size={12} aria-hidden="true" />
      {note.shift_type || 'Shift'} · {showDate(note.shift_date)}{note.start_time ? ` · ${fmtTime(note.start_time)}–${note.end_time ? fmtTime(note.end_time) : ''}` : ''}
    </p>
  );
}

function Ratings({ note }) {
  const acts = asArray(note.activities_completed);
  if (!(note.mood_rating > 0 || note.behaviour_rating > 0 || acts.length)) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {note.mood_rating > 0 && <span className="badge-gray">Mood {note.mood_rating}/5</span>}
      {note.behaviour_rating > 0 && <span className="badge-gray">Behaviour {note.behaviour_rating}/5</span>}
      {acts.map((a) => <span key={a} className="badge-gray">{a}</span>)}
    </div>
  );
}

export default function StaffNotes() {
  const { id } = useParams();
  const confirm = useConfirm();
  const noteId = useId();
  const { staff, error: staffError, reload: reloadStaff } = useStaffRecord(id);
  const [notes, setNotes] = useState([]);
  const [progressNotes, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('internal');
  const [viewNote, setViewNote] = useState(null);
  const closeNote = useStableCallback(() => setViewNote(null));

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    Promise.all([api.get(`/staff/${id}/notes`), api.get(`/staff/${id}/progress-notes`)])
      .then(([a, b]) => { setNotes(Array.isArray(a.data) ? a.data : []); setProgress(Array.isArray(b.data) ? b.data : []); })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load notes. Try again."))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function addNote(e) {
    if (e) e.preventDefault();
    if (!body.trim()) { toast.error('Write the note first'); return; }
    setSaving(true);
    try {
      await api.post(`/staff/${id}/notes`, { body: body.trim() });
      toast.success('Note added');
      setBody('');
      load();
    } catch (err) { toast.error(err.response?.data?.message || "The note wasn't added. Try again."); }
    finally { setSaving(false); }
  }

  async function deleteNote(n) {
    if (!await confirm('This internal note will be deleted. This can\'t be undone.', { title: 'Delete note?', confirmLabel: 'Delete', danger: true })) return;
    try {
      await api.delete(`/staff/${id}/notes/${n.id}`);
      toast.success('Note deleted');
      setNotes((list) => list.filter((x) => x.id !== n.id));
    } catch (err) { toast.error(err.response?.data?.message || "The note wasn't deleted. Try again."); }
  }

  const box = (child) => <div className="rounded-xl border border-slate-200 bg-white">{child}</div>;

  return (
    <div>
      <StaffHeader staff={staff} error={staffError} onRetry={reloadStaff} />
      <div className="space-y-4">
        <Tabs variant="underline" ariaLabel="Note type" value={tab} onChange={setTab} tabs={[
          { id: 'internal', label: 'Internal notes', count: loading ? undefined : notes.length },
          { id: 'progress', label: 'Progress notes they wrote', count: loading ? undefined : progressNotes.length },
        ]} />

        {loading ? box(<TableSkeleton rows={3} cols={2} />)
          : loadError ? box(<ErrorState message={loadError} onRetry={load} />)
            : tab === 'internal' ? (
              <>
                <form onSubmit={addNote} className="rounded-xl border border-slate-200 bg-white p-4">
                  <label htmlFor={noteId} className="mb-1.5 block text-[13px] font-medium text-slate-700">Add an internal note</label>
                  <p className="mb-2 text-[12px] text-slate-500">Only office staff see these. The worker doesn't.</p>
                  <textarea id={noteId} className="input-field resize-y" rows={3} value={body} onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addNote(); }} />
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className="text-[12px] text-slate-500">Ctrl + Enter to add</span>
                    <button type="submit" disabled={saving || !body.trim()} className="btn-primary btn-sm"><Plus size={14} aria-hidden="true" />{saving ? 'Adding…' : 'Add note'}</button>
                  </div>
                </form>
                {notes.length === 0 ? box(<EmptyState icon={PenLine} title="No internal notes yet" hint="Add the first one above." />) : (
                  <ul className="space-y-3">
                    {notes.map((n) => (
                      <li key={n.id} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                        <PersonAvatar id={n.created_by} name={n.created_by_name || 'Staff'} size="sm" />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2">
                            <span className="text-sm font-semibold text-slate-900">{n.created_by_name || 'Staff'}</span>
                            <span className="text-[12.5px] text-slate-500" title={n.created_at ? new Date(n.created_at).toLocaleString('en-AU') : undefined}>{fmtAgo(n.created_at)}</span>
                          </div>
                          <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{n.body}</p>
                        </div>
                        <IconButton label="Delete note" icon={Trash2} danger onClick={() => deleteNote(n)} />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <>
                <p className="text-[13px] text-slate-600">Every progress note this worker wrote on their shifts.</p>
                {progressNotes.length === 0 ? box(<EmptyState icon={FileText} title="No progress notes" hint="Notes they write in the app after a shift show here." />) : (
                  <ul className="space-y-3">
                    {progressNotes.map((note) => (
                      <li key={note.id} className="space-y-2 rounded-xl border border-slate-200 bg-white p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          {note.note_type && <span className="badge-gray">{note.note_type}</span>}
                          {note.client_name && <span className="text-sm font-semibold text-slate-900">{note.client_name}</span>}
                          {/* Age from the day the note COVERS, not the day it was typed. */}
                          <span className="ml-auto text-[12.5px] text-slate-500">{fmtAgo(note.note_date || note.shift_date || note.created_at)}</span>
                        </div>
                        <p className="line-clamp-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{note.note_content}</p>
                        <button type="button" onClick={() => setViewNote(note)} className="text-[13px] font-medium text-primary-700 hover:underline">Read full note</button>
                        <Ratings note={note} />
                        {asArray(note.attachments).length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {asArray(note.attachments).map((a, i) => (
                              <a key={i} href={a} target="_blank" rel="noreferrer" aria-label={`Open attachment ${i + 1}`}>
                                <img src={a} alt="" className="h-14 w-14 rounded-lg border border-slate-200 object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                        <ShiftLine note={note} />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
      </div>

      <Dialog open={!!viewNote} onClose={closeNote} size="md"
        title={viewNote ? (viewNote.note_type || 'Progress note') : ''}
        description={viewNote ? [viewNote.client_name, viewNote.shift_date && showDate(viewNote.shift_date), viewNote.start_time && `${fmtTime(viewNote.start_time)}–${viewNote.end_time ? fmtTime(viewNote.end_time) : ''}`].filter(Boolean).join(' · ') : ''}>
        {viewNote && (
          <div className="space-y-3">
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800">{viewNote.note_content}</p>
            <Ratings note={viewNote} />
            {asArray(viewNote.attachments).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {asArray(viewNote.attachments).map((a, i) => <a key={i} href={a} target="_blank" rel="noreferrer" aria-label={`Open attachment ${i + 1}`}><img src={a} alt="" className="h-20 w-20 rounded-lg border border-slate-200 object-cover" /></a>)}
              </div>
            )}
          </div>
        )}
      </Dialog>
    </div>
  );
}
